import React from "react";
import { Briefcase, GraduationCap, ArrowUpRight } from "lucide-react";
import portfolio from "../data/portfolio.json";

const { experiences } = portfolio;

const TimelineNode = ({ type }) => {
  const Icon = type === "education" ? GraduationCap : Briefcase;
  return (
    <div className="w-10 h-10 rounded-2xl bg-zinc-950 border border-zinc-800 group-hover:border-teal-300/40 flex items-center justify-center transition-colors shrink-0">
      <Icon className="w-4 h-4 text-zinc-500 group-hover:text-teal-300 transition-colors" />
    </div>
  );
};

const Experience = () => {
  return (
    <section id="experience" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg-dense opacity-40" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="flex items-center gap-4 mb-16">
          <span className="font-mono text-xs text-teal-300">04 / Experience</span>
          <div className="h-px flex-1 bg-gradient-to-r from-zinc-800 to-transparent" />
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="sticky top-32">
              <h2
                className="font-display font-bold text-white tracking-tight"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)" }}
            >
                Work, studies,{" "}
                <span className="text-teal-300">and a bit of context</span>.
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed hidden lg:block">
                The short version of where I've worked and what I worked on.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8 relative">
            {/* timeline line */}
            <div className="absolute left-5 top-2 bottom-2 w-px bg-gradient-to-b from-teal-300/40 via-zinc-800 to-transparent" />

            <div className="space-y-6">
              {experiences.map((exp, i) => (
                <div key={exp.id} className="relative pl-16 group">
                  {/* timeline node */}
                  <div className="absolute left-0 top-5">
                    <TimelineNode type={exp.type} />
                  </div>
                  {i === 0 && (
                    <span className="absolute left-0 top-5 w-10 h-10 rounded-2xl border border-teal-300/30 animate-ping opacity-40 pointer-events-none" />
                  )}

                  <a
                    href={exp.company.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block glass rounded-2xl p-6 transition-all duration-300 hover:bg-white/[0.04] border border-transparent"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${exp.company.color}55`;
                      e.currentTarget.style.boxShadow = `0 0 0 1px ${exp.company.color}22, 0 20px 60px -20px ${exp.company.color}33`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-lg md:text-xl text-zinc-300">
                          {exp.position}
                        </span>
                        <span
                          className="font-display text-lg md:text-xl font-semibold inline-flex items-baseline gap-1.5 group-hover:underline underline-offset-4"
                          style={{ color: exp.company.color }}
                        >
                          {exp.company.name}
                          <ArrowUpRight className="w-4 h-4 translate-y-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </div>
                      <span className="font-mono text-[11px] text-zinc-500 bg-zinc-900/80 border border-zinc-800 px-2.5 py-1 rounded-md whitespace-nowrap">
                        {exp.date}
                      </span>
                    </div>
                    {exp.highlights && exp.highlights.length > 0 ? (
                      <ul className="mt-4 space-y-2 text-sm text-zinc-400 leading-relaxed">
                        {exp.highlights.map((highlight) => (
                          <li key={highlight} className="flex gap-2.5">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-300/70" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                        {exp.description}
                      </p>
                    )}
                  </a>
                </div>
              ))}
            </div>

            <p className="mt-10 text-sm text-zinc-500 lg:hidden">
              Company names open in a new tab.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
